import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };

/* ------------------------------------------------------------------ */
/*  STAGE MAPPING                                                      */
/* ------------------------------------------------------------------ */

const WAITING_ON_US = [
  'docs_received', 'evidence_lock_signed', 'business_profile_review_pending',
  'clarity_call_pending', 'sentry_running',
  'presentation_1_pending', 'presentation_1_delivered',
  'presentation_2_pending', 'presentation_2_delivered',
  'report_ready', 'final_report_ready',
];

const WAITING_ON_THEM = [
  'engagement_opened', 'docs_requested', 'docs_pending',
  'intake_pending', 'payment_pending',
  'evidence_lock_sent', 'evidence_lock_pending',
  'presentation_1_scheduled', 'presentation_2_scheduled',
];

const CONFIRMED_STAGES = [
  'presented', 'complete', 'delivered',
];

function getWaitingInfo(eng) {
  const stage = eng.current_stage || 'engagement_opened';
  const name = eng.client_name || 'Client';

  const usMap = {
    docs_received:                     { who: 'Analyst', desc: 'Review documents' },
    business_profile_review_pending:   { who: 'Analyst', desc: 'Review business profile' },
    clarity_call_pending:              { who: 'Mira', desc: 'Schedule discovery call' },
    sentry_running:                    { who: 'Analyst', desc: 'Simulation running' },
    evidence_lock_signed:              { who: 'Mira', desc: 'Send Presentation Invite' },
    presentation_1_pending:            { who: 'Ron', desc: 'Deliver Presentation 1' },
    presentation_1_delivered:          { who: 'Analyst', desc: 'Prepare Retrade' },
    presentation_2_pending:            { who: 'Ron', desc: 'Deliver Presentation 2' },
    presentation_2_delivered:          { who: 'Analyst', desc: 'Build Intervention Index' },
    report_ready:                      { who: 'Ron', desc: 'Approve Report' },
    final_report_ready:                { who: 'Ron', desc: 'Approve Report' },
  };

  const themMap = {
    engagement_opened:                 { desc: 'Upload documents' },
    docs_requested:                    { desc: 'Upload documents' },
    docs_pending:                      { desc: 'Upload documents' },
    intake_pending:                    { desc: 'Complete intake form' },
    payment_pending:                   { desc: 'Complete payment' },
    evidence_lock_sent:                { desc: 'Sign Evidence Lock' },
    evidence_lock_pending:             { desc: 'Sign Evidence Lock' },
    presentation_1_scheduled:          { desc: 'Confirm booking' },
    presentation_2_scheduled:          { desc: 'Confirm booking' },
  };

  const confirmMap = {
    presented:                         { desc: 'Report Delivered' },
    complete:                          { desc: 'Engagement Complete' },
    delivered:                         { desc: 'Engagement Complete' },
  };

  if (usMap[stage]) {
    return { type: 'us', who: usMap[stage].who, desc: `${usMap[stage].who} -- ${usMap[stage].desc}`, badge: '#0A1828' };
  }
  if (themMap[stage]) {
    return { type: 'them', who: name, desc: `${name} -- ${themMap[stage].desc}`, badge: '#B5832A' };
  }
  if (confirmMap[stage]) {
    return { type: 'confirmed', who: '', desc: confirmMap[stage].desc, badge: '#1A7A62' };
  }
  return { type: 'us', who: 'Team', desc: 'Processing', badge: '#0A1828' };
}

const STAGE_LABELS = {
  engagement_opened: 'Awaiting Documents',
  docs_requested: 'Awaiting Documents',
  docs_pending: 'Awaiting Documents',
  intake_pending: 'Awaiting Intake',
  payment_pending: 'Awaiting Payment',
  docs_received: 'Document Review',
  evidence_lock_pending: 'Evidence Review',
  evidence_lock_sent: 'Evidence Review Sent',
  evidence_lock_signed: 'Evidence Signed',
  business_profile_review_pending: 'Profile Review',
  clarity_call_pending: 'Discovery Pending',
  sentry_running: 'Simulation Running',
  presentation_1_pending: 'Presentation 1',
  presentation_1_scheduled: 'Discovery Booked',
  presentation_1_delivered: 'Discovery Complete',
  presentation_2_pending: 'Presentation 2',
  presentation_2_scheduled: 'Retrade Booked',
  presentation_2_delivered: 'Retrade Complete',
  report_ready: 'Report Ready',
  final_report_ready: 'Report Ready',
  presented: 'Report Presented',
  complete: 'Complete',
  delivered: 'Complete',
};

function stageToStep(stage) {
  const map = {
    engagement_opened: 1, docs_requested: 1, docs_pending: 1,
    intake_pending: 1, payment_pending: 1,
    docs_received: 2, evidence_lock_pending: 2, evidence_lock_sent: 2,
    evidence_lock_signed: 3, business_profile_review_pending: 3,
    clarity_call_pending: 3, sentry_running: 3,
    presentation_1_pending: 4, presentation_1_scheduled: 4,
    presentation_1_delivered: 4,
    presentation_2_pending: 5, presentation_2_scheduled: 5,
    presentation_2_delivered: 5,
    report_ready: 6, final_report_ready: 6,
    presented: 7, complete: 7, delivered: 7,
  };
  return map[stage] || 1;
}

function daysSince(ts) {
  if (!ts) return 0;
  try { return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000); } catch { return 0; }
}

/* ------------------------------------------------------------------ */
/*  PROGRESS DOTS                                                      */
/* ------------------------------------------------------------------ */

function ProgressDots({ currentStep }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {[1, 2, 3, 4, 5, 6, 7].map(step => {
        const complete = step < currentStep;
        const isCurrent = step === currentStep;
        const dotSize = isCurrent ? 10 : 8;
        const dotColor = complete ? '#1A6B6B' : isCurrent ? '#7A5230' : 'transparent';
        const borderColor = complete ? '#1A6B6B' : isCurrent ? '#7A5230' : 'rgba(7,25,42,0.15)';
        const lineColor = complete ? '#1A6B6B' : 'rgba(7,25,42,0.10)';

        return (
          <React.Fragment key={step}>
            {step > 1 && (
              <div style={{ width: 8, height: 2, background: lineColor, flexShrink: 0 }} />
            )}
            <div style={{
              width: dotSize, height: dotSize,
              borderRadius: '50%',
              background: dotColor,
              border: `2px solid ${borderColor}`,
              boxSizing: 'border-box',
              flexShrink: 0,
            }} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SUMMARY CARDS                                                      */
/* ------------------------------------------------------------------ */

function SummaryCards({ engagements }) {
  const active = engagements.filter(e => {
    const s = e.current_stage || '';
    return s !== 'complete' && s !== 'delivered' && e.status !== 'complete';
  }).length;

  const waitingOnUs = engagements.filter(e =>
    WAITING_ON_US.includes(e.current_stage || '')
  ).length;

  const waitingOnThem = engagements.filter(e =>
    WAITING_ON_THEM.includes(e.current_stage || '')
  ).length;

  const confirmed = engagements.filter(e =>
    CONFIRMED_STAGES.includes(e.current_stage || '')
  ).length;

  const cards = [
    { label: 'ACTIVE', value: active, color: '#0A1828' },
    { label: 'WAITING ON US', value: waitingOnUs, color: '#0A1828' },
    { label: 'WAITING ON THEM', value: waitingOnThem, color: '#B5832A' },
    { label: 'CONFIRMED', value: confirmed, color: '#1A7A62' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: '#FAFAF8', border: '1px solid rgba(7,25,42,0.10)',
          padding: '20px 24px',
        }}>
          <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 8 }}>
            {c.label}
          </div>
          <div style={{ ...serif, fontSize: 40, fontWeight: 300, color: c.color, lineHeight: 1 }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ENGAGEMENT ROW                                                     */
/* ------------------------------------------------------------------ */

function EngagementRow({ eng }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const waiting = getWaitingInfo(eng);
  const stageLabel = STAGE_LABELS[eng.current_stage] || (eng.current_stage || 'unknown').replace(/_/g, ' ');
  const currentStep = stageToStep(eng.current_stage || 'engagement_opened');
  const days = daysSince(eng.updated_at || eng.created_at);
  const dayColor = days <= 3 ? '#1A7A62' : days <= 7 ? '#B5832A' : 'rgba(160,40,40,0.85)';

  const badgeBg = waiting.badge;
  const badgeLabel = waiting.type === 'us' ? 'WAITING ON US'
    : waiting.type === 'them' ? 'WAITING ON THEM'
    : 'CONFIRMED';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        minHeight: 64, padding: '16px 24px',
        borderBottom: '1px solid rgba(7,25,42,0.08)',
        background: hovered ? '#F3F2EF' : 'transparent',
        transition: 'background 0.12s',
      }}
    >
      {/* CLIENT INFO - 20% */}
      <div style={{ width: '20%', flexShrink: 0, minWidth: 0 }}>
        <div style={{ ...sans, fontSize: 14, color: '#0A1828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {eng.client_name || 'Unknown'}
        </div>
        <div style={{ ...mono, fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#7A5230', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {eng.client_company || eng.company || ''}
        </div>
      </div>

      {/* WAITING ON - 25% */}
      <div style={{ width: '25%', flexShrink: 0, minWidth: 0 }}>
        <span style={{
          ...mono, fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase',
          padding: '3px 10px', background: badgeBg, color: '#FFFFFF',
          display: 'inline-block', marginBottom: 4,
        }}>
          {badgeLabel}
        </span>
        <div style={{
          ...mono, fontSize: 9, color: badgeBg, opacity: 0.7,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {waiting.desc}
        </div>
      </div>

      {/* STAGE LABEL - 20% */}
      <div style={{ width: '20%', flexShrink: 0, minWidth: 0 }}>
        <div style={{ ...mono, fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#7A5230' }}>
          {stageLabel}
        </div>
      </div>

      {/* PROGRESS DOTS - 15% */}
      <div style={{ width: '15%', flexShrink: 0 }}>
        <ProgressDots currentStep={currentStep} />
      </div>

      {/* DAYS IN STAGE - 10% */}
      <div style={{ width: '10%', flexShrink: 0, textAlign: 'center' }}>
        <div style={{ ...mono, fontSize: 11, color: dayColor, fontWeight: 500 }}>
          {days} days
        </div>
      </div>

      {/* OPEN BUTTON - 10% */}
      <div style={{ width: '10%', flexShrink: 0, textAlign: 'right' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            localStorage.setItem('mri_engagement_id', eng.id);
            navigate(`/overview/${eng.id}`);
          }}
          style={{
            ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase',
            background: '#0A1828', color: '#FFFFFF', border: 'none',
            padding: '8px 16px', cursor: 'pointer', borderRadius: 0, height: 36,
          }}
        >
          OPEN
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  MOBILE ROW                                                         */
/* ------------------------------------------------------------------ */

function MobileEngagementRow({ eng }) {
  const navigate = useNavigate();
  const waiting = getWaitingInfo(eng);
  const stageLabel = STAGE_LABELS[eng.current_stage] || (eng.current_stage || '').replace(/_/g, ' ');
  const currentStep = stageToStep(eng.current_stage || 'engagement_opened');
  const days = daysSince(eng.updated_at || eng.created_at);
  const dayColor = days <= 3 ? '#1A7A62' : days <= 7 ? '#B5832A' : 'rgba(160,40,40,0.85)';
  const badgeBg = waiting.badge;
  const badgeLabel = waiting.type === 'us' ? 'WAITING ON US'
    : waiting.type === 'them' ? 'WAITING ON THEM' : 'CONFIRMED';

  return (
    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(7,25,42,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ ...sans, fontSize: 14, color: '#0A1828' }}>{eng.client_name || 'Unknown'}</div>
          <div style={{ ...mono, fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#7A5230', marginTop: 2 }}>
            {eng.client_company || eng.company || ''}
          </div>
        </div>
        <div style={{ ...mono, fontSize: 11, color: dayColor, fontWeight: 500 }}>{days}d</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ ...mono, fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 10px', background: badgeBg, color: '#FFFFFF' }}>
          {badgeLabel}
        </span>
        <span style={{ ...mono, fontSize: 9, color: '#7A5230', textTransform: 'uppercase' }}>{stageLabel}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ProgressDots currentStep={currentStep} />
        <button
          onClick={() => { localStorage.setItem('mri_engagement_id', eng.id); navigate(`/overview/${eng.id}`); }}
          style={{ ...mono, fontSize: 8, letterSpacing: '0.10em', textTransform: 'uppercase', background: '#0A1828', color: '#FFFFFF', border: 'none', padding: '8px 16px', cursor: 'pointer', borderRadius: 0, height: 36 }}
        >
          OPEN
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN                                                               */
/* ------------------------------------------------------------------ */

export default function Pipeline() {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('cordis_engagements')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setEngagements(data || []);
    } catch (err) {
      console.error('Failed to load engagements:', err);
      // Fallback to API
      try {
        const res = await fetch('/api/advisor/engagements', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setEngagements(Array.isArray(data) ? data : []);
        }
      } catch { /* silent */ }
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', background: '#FAFAF8', minHeight: '100vh' }}>
        <div style={{ ...mono, fontSize: 10, color: '#7A5230', letterSpacing: '0.14em' }}>LOADING...</div>
      </div>
    );
  }

  // Sort: active first, then by most recent update
  const sorted = [...engagements].sort((a, b) => {
    const aComplete = (a.current_stage === 'complete' || a.current_stage === 'delivered') ? 1 : 0;
    const bComplete = (b.current_stage === 'complete' || b.current_stage === 'delivered') ? 1 : 0;
    if (aComplete !== bComplete) return aComplete - bComplete;
    return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
  });

  return (
    <div style={{ background: '#FAFAF8', minHeight: '100vh', padding: '32px 40px', maxWidth: 1200 }}>

      {/* Header */}
      <div style={{ ...serif, fontSize: 32, fontWeight: 300, color: '#0A1828', marginBottom: 24 }}>
        Pipeline
      </div>

      {/* Summary Cards */}
      <SummaryCards engagements={engagements} />

      {/* Column Headers (desktop) */}
      {!isMobile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 0,
          padding: '10px 24px',
          borderBottom: '1px solid rgba(7,25,42,0.10)',
        }}>
          <div style={{ width: '20%', ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8' }}>CLIENT</div>
          <div style={{ width: '25%', ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8' }}>WAITING ON</div>
          <div style={{ width: '20%', ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8' }}>STAGE</div>
          <div style={{ width: '15%', ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8' }}>PROGRESS</div>
          <div style={{ width: '10%', ...mono, fontSize: 7.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#7A94A8', textAlign: 'center' }}>DAYS</div>
          <div style={{ width: '10%' }} />
        </div>
      )}

      {/* Rows */}
      {sorted.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8' }}>No engagements in pipeline</div>
        </div>
      ) : (
        sorted.map(eng => (
          isMobile
            ? <MobileEngagementRow key={eng.id} eng={eng} />
            : <EngagementRow key={eng.id} eng={eng} />
        ))
      )}
    </div>
  );
}
